import React, { useEffect, useState } from "react";
import { AlertTriangle, Flame } from "lucide-react";
import api from "../lib/api";
import { Label, EmptyState } from "./common";

/**
 * Criteria dragging recovery cases.
 * Shows which touchpoint dimensions fired the per-dim recovery trigger most often in the
 * window, so admins can see at a glance which criterion is hurting them the hardest.
 */
export default function TriggerLeaderboard({ days = 30, testId = "trigger-leaderboard" }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get(`/recovery/trigger-leaderboard?days=${days}`)
      .then((r) => !cancelled && setData(r.data))
      .catch(() => !cancelled && setData({ items: [], total_cases: 0 }));
    return () => { cancelled = true; };
  }, [days]);

  if (!data) return null;
  const top = data.items.slice(0, 5);
  const max = top[0]?.count || 1;

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5" data-testid={testId}>
      <div className="mb-3 flex items-start justify-between gap-2">
        <div>
          <Label>Criteria dragging recovery cases</Label>
          <p className="mt-1 text-xs text-slate-500">
            Which touchpoint criteria fired the recovery trigger most in the last {days} days.
          </p>
        </div>
        <AlertTriangle className="h-4 w-4 text-rose-500" />
      </div>

      {top.length === 0 ? (
        <EmptyState icon={Flame} title="Nothing dragging you down"
          hint="No per-dim recovery triggers fired in this window." />
      ) : (
        <ol className="space-y-2" data-testid={`${testId}-list`}>
          {top.map((d, idx) => {
            const pctWidth = Math.max(6, Math.round(100 * d.count / max));
            return (
              <li key={d.key} className="flex items-center gap-3" data-testid={`${testId}-row-${d.key}`}>
                <span className="w-5 shrink-0 text-right text-xs font-bold text-slate-400">{idx + 1}.</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-semibold text-slate-800">{d.label}</span>
                    <span className="shrink-0 text-xs text-slate-500">
                      <b className="text-slate-800">{d.count}</b> case{d.count === 1 ? "" : "s"}
                      {d.open > 0 && <span className="ml-2 rounded-full bg-rose-50 px-1.5 py-0.5 text-[11px] font-bold text-rose-600">{d.open} open</span>}
                      <span className="ml-2 text-slate-400">· {d.pct}%</span>
                    </span>
                  </div>
                  <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-slate-100">
                    <div className="h-full rounded-full bg-rose-500" style={{ width: `${pctWidth}%` }} />
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}
